import { packTotalCents, type ListedPiece } from "./catalog-types";

/**
 * The order of the host's list, as the prototype offers it ("Ordenar a lista").
 *
 * It opens on the curation order — featured pieces first, the rest as the list holds them — and
 * the person may re-order by the pack's price, by name, or by how many were gifted. Labels are
 * literal from the prototype's management context.
 */

export type CatalogOrder = "curadoria" | "menor" | "maior" | "az" | "za" | "presenteados";

export const CATALOG_ORDERS: readonly { key: CatalogOrder; label: string }[] = [
  { key: "curadoria", label: "Ordem da curadoria" },
  { key: "menor", label: "Preço · menor" },
  { key: "maior", label: "Preço · maior" },
  { key: "az", label: "Nome · A a Z" },
  { key: "za", label: "Nome · Z a A" },
  { key: "presenteados", label: "Mais presenteados" },
];

/**
 * The orders a guest is offered in the host's preview: the prototype renames the first ("Ordem do
 * anfitrião") and drops "Mais presenteados", which reads the host's own numbers.
 */
export const GUEST_CATALOG_ORDERS: readonly { key: CatalogOrder; label: string }[] = [
  { key: "curadoria", label: "Ordem do anfitrião" },
  ...CATALOG_ORDERS.filter((order) => order.key !== "curadoria" && order.key !== "presenteados"),
];

/**
 * The orders of a house suggestion list: there is no price to order by, and nothing was gifted —
 * the prototype keeps the curation's own order and the two by name (`semPreco`).
 */
export const SUGGESTION_CATALOG_ORDERS: readonly { key: CatalogOrder; label: string }[] = CATALOG_ORDERS.filter(
  (order) => order.key === "curadoria" || order.key === "az" || order.key === "za",
);

/**
 * The orders of the creation's curation: the curation's own order and the name both ways.
 *
 * **No price.** Filters handoff §6 offers "Preço · menor / maior" only in "contextos que exibem
 * preço", and the prototype's `ORDENS` excludes it from `curadoria` (`03-prototipo-catalogo.logic.js`,
 * `if (!semPreco && !adminCtx && !curadoria)`). This list used to offer it because the card does
 * draw a price here; the designer's rule is about the context, not the card, and it prevails.
 * "Mais presenteados" reads gifts, and a list being born has none.
 */
export const CURATION_CATALOG_ORDERS: readonly { key: CatalogOrder; label: string }[] = CATALOG_ORDERS.filter(
  (order) => order.key === "curadoria" || order.key === "az" || order.key === "za",
);

/** Who is looking at the catalog, as far as its order control is concerned. */
export type CatalogOrderAudience = "gestao" | "convidado" | "sugestao" | "curadoria";

/**
 * Whether the audience's context offers price as a filter and as an order (filters spec §5, §6;
 * prototype `semValor`). A suggestion has no price at all, and the curation hides it by ruling.
 */
export function audienceOffersPrice(audience: CatalogOrderAudience): boolean {
  return audience !== "sugestao" && audience !== "curadoria";
}

/** The order options each audience is offered (filters spec §6, prototype `ORDENS`). */
export function catalogOrdersFor(audience: CatalogOrderAudience): readonly { key: CatalogOrder; label: string }[] {
  if (audience === "convidado") return GUEST_CATALOG_ORDERS;
  if (audience === "sugestao") return SUGGESTION_CATALOG_ORDERS;
  if (audience === "curadoria") return CURATION_CATALOG_ORDERS;

  return CATALOG_ORDERS;
}

export function parseCatalogOrder(value: string | undefined): CatalogOrder {
  return CATALOG_ORDERS.some((order) => order.key === value) ? (value as CatalogOrder) : "curadoria";
}

const COMPARE: Record<CatalogOrder, (a: ListedPiece, b: ListedPiece) => number> = {
  // Stable: two featured pieces, or two that are not, keep the order they came in.
  curadoria: (a, b) => Number(b.featured) - Number(a.featured),
  menor: (a, b) => packTotalCents(a) - packTotalCents(b),
  maior: (a, b) => packTotalCents(b) - packTotalCents(a),
  az: (a, b) => a.name.localeCompare(b.name, "pt-BR"),
  za: (a, b) => b.name.localeCompare(a.name, "pt-BR"),
  presenteados: (a, b) => b.purchased - a.purchased,
};

/** A new array in the chosen order; `Array.prototype.sort` is stable, as the curation order needs. */
export function orderPieces(pieces: readonly ListedPiece[], order: CatalogOrder): ListedPiece[] {
  return [...pieces].sort(COMPARE[order]);
}
